import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

/**
 * One "Get started" poster, drawn full size.
 *
 * Each slide is one flat graphic with the words set over it — the shape a
 * printed poster has — rather than an icon in a box beside a paragraph. The
 * whole tile is the artwork, so the radius is on the card and the art bleeds
 * to every edge of it.
 *
 * ⚠️ THE POSTERS ARE DELIBERATELY THEME-INDEPENDENT. A poster is a printed
 * object: it keeps its own paper and ink in both themes. They are
 * calimero.network's panels — charcoal paper, lime line-art, near-white type
 * — so on a light page they read as the landing's dark bands do, and on a
 * dark page as one of its cards.
 */

export type Poster = {
  /** Chosen by variant, not by prop: a caller cannot invent a sixth palette. */
  art: PosterVariant;
  eyebrow: string;
  title: string;
  body: string;
  /** Three short facts, set as chips under the copy. A wide slide with only
   *  a title and a sentence in it is mostly empty paper. */
  chips: string[];
  /** The words on the button. It is decoration — the whole slide is the
   *  link — so it must never be a nested <button> or a second anchor. */
  cta: string;
  href: string;
  /** In-app routes go through the router; anything else opens a tab. */
  internal?: boolean;
};

type PosterVariant = 'desktop' | 'docs' | 'publish' | 'explore' | 'source';

type Palette = {
  /** Two paper tones, top-left to bottom-right. */
  from: string;
  to: string;
  /** The raised surface a motif is drawn on (a page, a window, a card). */
  paper: string;
  /** The one saturated shape colour. */
  mark: string;
  /** Line work and type. Near-black, never pure. */
  ink: string;
};

// One palette, the landing's: the variants differ by their drawing, not by
// colour, the way calimero.network's illustrations do.
const CHARCOAL: Palette = {
  from: '#1f1e22',
  to: '#141316',
  paper: '#2a282e',
  mark: '#a5ff11',
  ink: '#fcfcfc',
};

const PALETTES: Record<PosterVariant, Palette> = {
  desktop: CHARCOAL,
  docs: CHARCOAL,
  publish: CHARCOAL,
  explore: CHARCOAL,
  source: CHARCOAL,
};

export function PosterCard({
  poster,
  active,
}: {
  poster: Poster;
  /**
   * Off-screen slides stay in the DOM — that is what lets the track slide —
   * but they must not take focus or be read out, or tabbing walks off the
   * side of the gallery into three invisible links.
   */
  active: boolean;
}) {
  const p = PALETTES[poster.art];

  const inner = (
    <>
      <PosterArt variant={poster.art} palette={p} />

      {/* The type sits on the artwork. A scrim rather than a solid band: the
          poster has to stay one image, and the words run along the
          bottom-left where the compositions leave room.

          ⚠️ TWO SCRIMS, BECAUSE THE SLIDE CHANGES SHAPE. A gradient running
          left-to-right clears the artwork on a 21:9 desktop slide, where the
          motif is a third of the way off to the right. On a phone the slide
          is 4:3 and the same motif sits directly behind the body copy — the
          laptop's dark keys read straight through the sentence. Below `sm`
          the wash comes up from the bottom instead, which is where the type
          actually is at that width. A CSS gradient cannot be varied by media
          query from an inline style, hence two elements. */}
      <span
        aria-hidden='true'
        className='absolute inset-0 sm:hidden'
        style={{
          background: `linear-gradient(to top, ${p.from}fa 0%, ${p.from}f2 46%, ${p.from}a6 72%, ${p.from}1a 100%)`,
        }}
      />
      <span
        aria-hidden='true'
        className='absolute inset-0 hidden sm:block'
        style={{
          background: `linear-gradient(100deg, ${p.from}f7 0%, ${p.from}d9 38%, ${p.from}40 68%, transparent 100%)`,
        }}
      />

      <span className='relative flex h-full max-w-[34rem] flex-col justify-end gap-2 p-6 sm:p-9'>
        <span
          className='text-[13px] font-normal uppercase tracking-[0.28em]'
          style={{ color: p.mark }}
        >
          {poster.eyebrow}
        </span>
        <span
          className='text-[26px] font-black uppercase leading-[1.05] tracking-[0.01em] sm:text-[36px]'
          style={{ color: p.ink }}
        >
          {poster.title}
        </span>
        <span
          className='text-[15.5px] font-light leading-relaxed sm:text-[17px]'
          style={{ color: p.ink, opacity: 0.75 }}
        >
          {poster.body}
        </span>

        <span className='mt-1 flex flex-wrap items-center gap-1.5'>
          {poster.chips.map(chip => (
            <span
              key={chip}
              className='border px-2 py-1 text-[11.5px] font-bold uppercase leading-none tracking-[0.15em]'
              style={{
                color: p.ink,
                borderColor: 'rgb(252 252 252 / 0.25)',
                opacity: 0.8,
              }}
            >
              {chip}
            </span>
          ))}
        </span>

        {/* A span, not a button: the entire slide is already one link, and a
            control inside it would be a second tab stop pointing at the same
            place. */}
        <span
          className='mt-3 inline-flex w-fit items-center gap-2 px-5 py-3 text-[13px] font-bold uppercase tracking-[0.15em] text-[#131215] transition-shadow duration-300 group-hover:shadow-[0_0_20px_0_rgba(165,255,17,0.45)]'
          style={{ background: p.mark }}
        >
          {poster.cta}
          <ArrowUpRight className='h-4 w-4' aria-hidden='true' />
        </span>
      </span>
    </>
  );

  // `overflow-hidden` on the anchor is what actually clips the artwork to the
  // radius — the SVG is a rectangle and would otherwise square off the corners.
  const className =
    'group relative flex h-full w-full flex-col overflow-hidden border border-line ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/60';

  if (poster.internal) {
    return (
      <Link
        to={poster.href}
        data-testid='promo-tile'
        tabIndex={active ? 0 : -1}
        aria-hidden={active ? undefined : true}
        className={className}
      >
        {inner}
      </Link>
    );
  }

  return (
    <a
      href={poster.href}
      target='_blank'
      rel='noreferrer'
      data-testid='promo-tile'
      tabIndex={active ? 0 : -1}
      aria-hidden={active ? undefined : true}
      className={className}
    >
      {inner}
    </a>
  );
}

/**
 * One flat composition per variant.
 *
 * Static: the hero already runs a timeline, and five more looping graphics on
 * the same screen is both noise and five more permanently live animations.
 */
function PosterArt({
  variant,
  palette: p,
}: {
  variant: PosterVariant;
  palette: Palette;
}) {
  // ⚠️ A WIDE viewBox, ANCHORED RIGHT. The slides are 21:9 on a desktop and
  // 4:3 on a phone; a 4:3 drawing sliced into a 21:9 box loses the top and
  // bottom of the motif and blows the rest up to twice its intended size.
  // Drawing at 420x180 and slicing from `xMaxYMid` keeps the motif intact at
  // every width — and the left, which is what gets cropped on a narrow
  // screen, is where the type sits anyway.
  return (
    <svg
      viewBox='0 0 420 180'
      preserveAspectRatio='xMaxYMid slice'
      className='absolute inset-0 h-full w-full'
      aria-hidden='true'
    >
      <defs>
        <linearGradient id={`poster-${variant}`} x1='0' y1='0' x2='1' y2='1'>
          <stop offset='0%' stopColor={p.from} />
          <stop offset='100%' stopColor={p.to} />
        </linearGradient>
      </defs>
      <rect width='420' height='180' fill={`url(#poster-${variant})`} />
      {/* Two quiet shapes under the type, so the left of a wide slide is
          composed rather than blank. */}
      <circle cx='40' cy='150' r='96' fill={p.mark} opacity='0.07' />
      <circle cx='150' cy='-20' r='70' fill={p.mark} opacity='0.06' />
      <g transform='translate(178 0)'>
        {variant === 'desktop' && <DesktopPoster p={p} />}
        {variant === 'docs' && <DocsPoster p={p} />}
        {variant === 'publish' && <PublishPoster p={p} />}
        {variant === 'explore' && <ExplorePoster p={p} />}
        {variant === 'source' && <SourcePoster p={p} />}
      </g>
    </svg>
  );
}

/** A screen, opened, with one tile lit inside it. */
function DesktopPoster({ p }: { p: Palette }) {
  return (
    <g>
      <circle cx='196' cy='34' r='46' fill={p.mark} opacity='0.16' />
      <rect
        x='52'
        y='26'
        width='136'
        height='84'
        fill={p.paper}
        opacity='0.55'
      />
      <rect
        x='52'
        y='26'
        width='136'
        height='84'
        fill='none'
        stroke={p.mark}
        strokeWidth='2'
        opacity='0.7'
      />
      <line
        x1='52'
        y1='44'
        x2='188'
        y2='44'
        stroke={p.mark}
        strokeWidth='1.5'
        opacity='0.45'
      />
      {[0, 1, 2].map(i => (
        <rect
          key={i}
          x={66 + i * 42}
          y='58'
          width='30'
          height='30'
          fill={i === 1 ? p.mark : p.ink}
          opacity={i === 1 ? '0.9' : '0.14'}
        />
      ))}
      <rect x='96' y='114' width='48' height='6' fill={p.ink} opacity='0.18' />
    </g>
  );
}

/** An open manual, one page carrying a code block. */
function DocsPoster({ p }: { p: Palette }) {
  return (
    <g>
      <circle cx='44' cy='40' r='40' fill={p.mark} opacity='0.14' />
      <path
        d='M120 40c-16-12-34-14-56-12v78c22-2 40 0 56 12z'
        fill={p.paper}
        opacity='0.6'
      />
      <path
        d='M120 40c16-12 34-14 56-12v78c-22-2-40 0-56 12z'
        fill={p.paper}
        opacity='0.45'
      />
      <path
        d='M120 40c-16-12-34-14-56-12v78c22-2 40 0 56 12c16-12 34-14 56-12V28c-22-2-40 0-56 12z'
        fill='none'
        stroke={p.mark}
        strokeWidth='2'
        strokeLinejoin='round'
        opacity='0.7'
      />
      <line
        x1='120'
        y1='40'
        x2='120'
        y2='118'
        stroke={p.mark}
        strokeWidth='1.5'
        opacity='0.5'
      />
      {[0, 1, 2].map(i => (
        <rect
          key={i}
          x='76'
          y={52 + i * 12}
          width={i === 2 ? 20 : 32}
          height='4'
          fill={p.ink}
          opacity='0.2'
        />
      ))}
      <rect x='132' y='50' width='36' height='30' fill={p.mark} opacity='0.8' />
      {[0, 1, 2].map(i => (
        <rect
          key={i}
          x='138'
          y={57 + i * 8}
          width={[22, 14, 18][i]}
          height='3.4'
          fill={p.paper}
          opacity='0.85'
        />
      ))}
    </g>
  );
}

/** A bundle leaving the machine — signed, versioned, on its way up. */
function PublishPoster({ p }: { p: Palette }) {
  return (
    <g>
      <circle cx='60' cy='132' r='52' fill={p.mark} opacity='0.14' />
      <path
        d='M120 22l40 24v48l-40 24-40-24V46z'
        fill={p.paper}
        opacity='0.55'
      />
      <path
        d='M120 22l40 24v48l-40 24-40-24V46z'
        fill='none'
        stroke={p.mark}
        strokeWidth='2'
        strokeLinejoin='round'
        opacity='0.75'
      />
      <path
        d='M80 46l40 24 40-24M120 70v48'
        fill='none'
        stroke={p.mark}
        strokeWidth='1.6'
        opacity='0.5'
      />
      <circle cx='120' cy='70' r='13' fill={p.mark} opacity='0.9' />
      <path
        d='M114 70l4.5 4.5L127 66'
        fill='none'
        stroke='#ffffff'
        strokeWidth='2.6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      {[0, 1, 2].map(i => (
        <rect
          key={i}
          x={186}
          y={44 + i * 14}
          width={[30, 22, 26][i]}
          height='5'
          fill={p.ink}
          opacity='0.16'
        />
      ))}
    </g>
  );
}

/** A shelf of apps, one picked out. */
function ExplorePoster({ p }: { p: Palette }) {
  return (
    <g>
      <circle cx='206' cy='140' r='48' fill={p.mark} opacity='0.16' />
      {[0, 1, 2, 3, 4, 5].map(i => {
        const col = i % 3;
        const row = Math.floor(i / 3);
        const on = i === 4;
        return (
          <g key={i}>
            <rect
              x={44 + col * 56}
              y={30 + row * 56}
              width='44'
              height='44'
              fill={on ? p.mark : '#ffffff'}
              opacity={on ? '0.92' : '0.55'}
            />
            <rect
              x={44 + col * 56}
              y={30 + row * 56}
              width='44'
              height='44'
              fill='none'
              stroke={p.mark}
              strokeWidth='1.6'
              opacity='0.55'
            />
          </g>
        );
      })}
      <circle
        cx='150'
        cy='108'
        r='26'
        fill='none'
        stroke={p.ink}
        strokeWidth='3'
        opacity='0.28'
      />
      <line
        x1='169'
        y1='127'
        x2='184'
        y2='142'
        stroke={p.ink}
        strokeWidth='3'
        strokeLinecap='round'
        opacity='0.28'
      />
    </g>
  );
}

/** Peers, connected directly — the thing the code in the repo actually does. */
function SourcePoster({ p }: { p: Palette }) {
  const nodes = [
    [72, 46],
    [168, 38],
    [196, 108],
    [104, 122],
    [40, 100],
  ];
  return (
    <g>
      <circle cx='120' cy='40' r='54' fill={p.mark} opacity='0.14' />
      {nodes.map(([x1, y1], i) =>
        nodes
          .slice(i + 1)
          .map(([x2, y2], j) => (
            <line
              key={`${i}-${j}`}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={p.ink}
              strokeWidth='1.4'
              opacity='0.18'
            />
          ))
      )}
      {nodes.map(([cx, cy], i) => (
        <g key={i}>
          <circle
            cx={cx}
            cy={cy}
            r={i === 0 ? 17 : 13}
            fill={p.paper}
            opacity='0.6'
          />
          <circle
            cx={cx}
            cy={cy}
            r={i === 0 ? 17 : 13}
            fill={i === 0 ? p.mark : 'none'}
            opacity={i === 0 ? '0.9' : '1'}
            stroke={p.mark}
            strokeWidth='2'
          />
        </g>
      ))}
    </g>
  );
}
